import { supabase } from '../lib/supabase';
import { taskPhotoService } from './taskPhotoService';

// Thin back-compat shim over taskPhotoService.
//
// Historically there were two parallel photo-upload services writing to two
// separate buckets/tables (task-completion-photos and task-photos). They
// represented the same concept from different product flows. This module
// now delegates all new uploads to taskPhotoService (bucket: task-photos,
// table: task_photos) so there is one source of truth for future photos.
//
// Existing task_completion_photos rows (uploaded before this consolidation)
// remain readable — daily-site-report unions both tables for display. This
// shim reads from the new location only; legacy rows are surfaced by other
// consumers that are already aware of both tables.
//
// Kept as a shim (rather than deleted) because several callers import it
// directly. Removing them is a separate follow-up.
//
// Error-surfacing pattern: see canonical doc-comment at the top of
// src/services/clientService.js. This shim now propagates the real error
// objects produced by taskPhotoService rather than wrapping them in strings.

function surfaceError(scope, error) {
  console.error(`[taskCompletionPhotosService] ${scope} failed:`, {
    code: error?.code,
    message: error?.message,
    details: error?.details,
    hint: error?.hint,
  });
  return error;
}

export const taskCompletionPhotosService = {
  /**
   * Legacy signature: (taskId, file, userId).
   * Delegates to taskPhotoService.uploadPhoto, looking up the task's site_id
   * so the row is correctly indexed for the site photo gallery.
   */
  async uploadCompletionPhoto(taskId, file, userId) {
    try {
      let siteId = null;
      if (taskId) {
        const { data: task, error: taskErr } = await supabase
          ?.from('daily_tasks')
          ?.select('site_id')
          ?.eq('id', taskId)
          ?.maybeSingle();
        if (taskErr) throw taskErr;
        siteId = task?.site_id ?? null;
      }

      // task_photos.site_id is NOT NULL. If the worker can't SELECT the task
      // (e.g. not the assignee), the RLS-scoped lookup above yields null and
      // the insert fails with an opaque constraint error. Bail early with a
      // typed error so the UI can surface something actionable.
      if (!siteId) {
        const err = new Error('Could not resolve site for this task. Please contact support.');
        err.code = 'NO_SITE_RESOLVED';
        return { data: null, error: surfaceError('uploadCompletionPhoto:resolveSite', err) };
      }

      const result = await taskPhotoService?.uploadPhoto(file, taskId, siteId);
      if (result?.error) {
        // taskPhotoService already logged via its own surfaceError.
        // Pass through the real error object unchanged.
        return { data: null, error: result?.error };
      }

      return { data: result?.data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('uploadCompletionPhoto', error) };
    }
  },

  /**
   * Legacy signature: returns photos for a task with `signedUrl` attached.
   * TaskDetailModal reads: id, signedUrl, uploader.full_name, uploaded_at.
   * Adapt the taskPhotoService shape so the modal keeps working without
   * changes: alias `createdAt` → `uploaded_at`, ensure `signedUrl` present.
   */
  async getPhotosForTask(taskId) {
    try {
      const { data, error } = await taskPhotoService?.getPhotosByTask(taskId);
      if (error) return { data: [], error };

      const photosWithUrls = await Promise.all(
        (data || [])?.map(async (photo) => {
          const { url, error: urlErr } = await taskPhotoService?.getSignedUrl(photo?.filePath, 3600);
          if (urlErr) {
            console.error('[taskCompletionPhotosService] thumbnail signed URL failed:', { photoId: photo?.id, code: urlErr?.code, message: urlErr?.message });
          }
          return {
            ...photo,
            signedUrl: url || null,
            uploaded_at: photo?.createdAt || null,
            file_path: photo?.filePath || null,
          };
        })
      );

      return { data: photosWithUrls, error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getPhotosForTask', error) };
    }
  },

  /**
   * Legacy signature: deletePhoto(photoId). taskPhotoService.deletePhoto
   * requires the file path too — look it up first.
   */
  async deletePhoto(photoId) {
    try {
      const { data: photo, error: fetchError } = await supabase
        ?.from('task_photos')
        ?.select('file_path')
        ?.eq('id', photoId)
        ?.maybeSingle();

      if (fetchError) throw fetchError;

      // If not in task_photos, fall back to the legacy task_completion_photos
      // table so delete still works for historical rows.
      if (!photo) {
        const { data: legacy, error: legacyFetchError } = await supabase
          ?.from('task_completion_photos')
          ?.select('file_path')
          ?.eq('id', photoId)
          ?.maybeSingle();
        if (legacyFetchError) throw legacyFetchError;
        if (legacy?.file_path) {
          await supabase?.storage?.from('task-completion-photos')?.remove([legacy?.file_path]);
        }
        const { error: delError } = await supabase
          ?.from('task_completion_photos')
          ?.delete()
          ?.eq('id', photoId);
        if (delError) throw delError;
        return { error: null };
      }

      const { error: delError } = await taskPhotoService?.deletePhoto(photoId, photo?.file_path);
      if (delError) throw delError;
      return { error: null };
    } catch (error) {
      return { error: surfaceError('deletePhoto', error) };
    }
  },
};
